import { authenticatedMiddleware } from '../middlewares/authenticatedMiddleware.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';
import { checkPermissionMiddleware } from '../middlewares/checkPermissionMiddleware.js';
import { Permission, type Role } from '../roles.js';
import { resolveRecalculateRolesForListSelection } from '../server/resolvers/resolveRecalculateRolesForListSelection.js';
import { resolveRecalculateRolesForPersonID } from '../server/resolvers/resolveRecalculateRolesForPersonID.js';
import { hasUserRole } from '../server/hasUserRole.js';
import { resolveSaveRolesForPerson } from '../server/resolvers/resolveSaveRolesForPerson.js';

const recalculateRolesForPersonID = registerMutation({
  operationName: 'recalculateRolesForPersonID',
  /**
   * Only an authentication floor, unlike its two siblings which really do change roles. This one
   * recomputes roles from data that was already permission-checked when it was written, and it is
   * triggered as follow-up work by contract saves, invite flows and the interview UI - callers who
   * legitimately hold no CHANGE_ROLES. It previously had no guard at all and was reachable
   * anonymously, which is what actually needed closing.
   */
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [{ type: () => String, name: 'personID' }],
  resolve: (personID: string) => async () => {
    await resolveRecalculateRolesForPersonID(personID)();
    return true;
  },
});

const recalculateRolesForListSelection = registerMutation({
  operationName: 'recalculateRolesForListSelection',
  type: () => Boolean,
  params: [{ type: () => UserListSelectionInterface, name: 'listSelection' }],
  middlewares: [checkPermissionMiddleware(Permission.CHANGE_ROLES, ANY_CONTEXT)],
  resolve: resolveRecalculateRolesForListSelection,
});

const saveRoleForPerson = registerMutation({
  operationName: 'saveRolesForPerson',
  type: () => Boolean,
  params: [
    { type: () => String, name: 'personID' },
    { type: () => String, name: 'role' },
    { type: () => [String], name: 'contexts' },
    { type: () => Boolean, name: 'remove' },
  ],
  middlewares: [checkPermissionMiddleware(Permission.CHANGE_ROLES, ANY_CONTEXT)],
  resolve: resolveSaveRolesForPerson,
});

const checkUserRole = registerQuery({
  operationName: 'checkUserRole',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [
    { type: () => String, name: 'personID' },
    { type: () => String, name: 'roleName' },
    { type: () => String, name: 'roleContext' },
  ],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  resolve: (personID: string, roleName: Role, roleContext: string) => () =>
    hasUserRole(personID, roleName, roleContext),
});

export const PermissionAPI = {
  recalculateForPerson: recalculateRolesForPersonID,
  recalculateForListSelection: recalculateRolesForListSelection,
  saveRoleForPerson,
  checkUserRole,
};
